import React, { useEffect } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { useScenarios } from '../../context/ScenarioContext';
import { EnhancedToggle } from '../common/EnhancedToggle';
import { Switch } from '@mui/material';
import './ChartDisplayOptions.css';


const ChartDisplayOptions = () => {
  const { 
    scenarioVisibility, 
    initScenarioVisibility, 
    toggleScenarioVisibility, 
    globalSettings, 
    handleGlobalSettingChange,
    visibilitySettings,
    toggleVisibilitySetting
  } = useSettings();
  const { scenarios, toggleScenarioVisibility: toggleScenarioDisplay } = useScenarios();
  
  // Initialize visibility settings for each scenario
  useEffect(() => {
    scenarios.forEach(scenario => {
      initScenarioVisibility(scenario.id);
    });
  }, [scenarios, initScenarioVisibility]);
  
  // Data types configuration with labels and colors - MATCHED TO ResultsChart colors
  const dataTypes = [
    { id: 'showSalary', label: 'Salary', color: 'rgba(59, 130, 246, 0.7)' },  // Blue
    { id: 'showPension', label: 'Pension', color: 'rgba(99, 102, 241, 0.7)' },  // Indigo
    { id: 'showTsp', label: 'TSP', color: 'rgba(139, 92, 246, 0.7)' },  // Purple
    { id: 'showTSPBalance', label: 'TSP Balance', color: 'rgba(139, 92, 246, 0.5)' },  // Lighter Purple
    { id: 'showFERSSupplement', label: 'FERS Supplement', color: 'rgba(167, 139, 250, 0.7)' }, // Light Purple
    { id: 'showSocialSecurity', label: 'Social Security', color: 'rgba(196, 181, 253, 0.7)' },  // Lavender
    { id: 'showAdditionalIncome', label: 'Additional Income', color: 'rgba(160, 174, 192, 0.7)' }, // Slate Gray - new
    { id: 'showTotal', label: 'Total Income', color: '#000000' }  // Black
  ];
  
  // Toggle all visibility settings for a scenario
  const toggleAllForScenario = (scenarioId, newState) => {
    dataTypes.forEach(dataType => {
      // Only toggle if the current state doesn't match the desired state
      const currentState = scenarioVisibility[scenarioId][dataType.id];
      if (currentState !== newState) {
        toggleScenarioVisibility(scenarioId, dataType.id);
      }
    });
  };

  // Style for toggle blocks
  const getToggleBlockStyle = (isActive, color) => ({
    width: '20px',
    height: '20px',
    backgroundColor: color,
    opacity: isActive ? 1 : 0.3,
    border: '1px solid #ddd',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative'
  });


  const handleCheckboxChange = (field, checked) => {
    handleGlobalSettingChange(field, checked);
  };

  return (
    <div className="chart-display-options">

      
      {/* --- Income Delta Toggle --- */}
      <div style={{ marginBottom: '15px' }}>
          <EnhancedToggle
            checked={visibilitySettings.showIncomeDelta}
            onChange={() => toggleVisibilitySetting('showIncomeDelta')}
            size="small"
            label="Show Income Delta (Scenario 1 vs 2)"
        />
      </div>
      {/* --- End Income Delta Toggle --- */}

      {/* New table-like layout for projection data toggles */}
      <div className="projection-toggle-grid">
        {/* Table header - only visible on desktop */}
        <div className="grid-header">
          <div className="scenario-cell">Scenarios</div>
          {dataTypes.map(dataType => (
            <div key={`header-${dataType.id}`} className="data-type-header">
              <div className="data-type-color" style={{ backgroundColor: dataType.color }}></div>
              <div className="data-type-label">{dataType.label}</div>
            </div>
          ))}
        </div>

        {/* Scenario rows */}
        {scenarios.map(scenario => {
          const settings = scenarioVisibility[scenario.id] || {};
          const scenarioColor = scenario.color || '#1976d2';
          const allEnabled = dataTypes.every(dt => settings[dt.id] === true);
          
          return (
            <div key={scenario.id} className="grid-row">
              {/* Scenario name cell with toggle all ability */}
              <div 
                className="scenario-cell"
                onClick={() => toggleAllForScenario(scenario.id, !allEnabled)}
                style={{ 
                  cursor: 'pointer', 
                  fontWeight: 'bold',
                  color: scenario.visible ? '#333' : '#aaa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                title={`Click to ${allEnabled ? 'disable' : 'enable'} all data types for ${scenario.name}`}
              >
                <Switch 
                  checked={scenario.visible}
                  onChange={() => toggleScenarioDisplay(scenario.id)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: scenarioColor,
                      '&:hover': { backgroundColor: `${scenarioColor}20` },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: scenarioColor,
                    },
                  }}
                />
                <span>{scenario.name}</span>
              </div>
              
              {/* Mobile: Show data type labels and toggles in a responsive grid */}
              <div className="mobile-toggles">
                {dataTypes.map(dataType => {
                  const isActive = settings[dataType.id];
                  
                  return (
                    <div key={`mobile-${scenario.id}-${dataType.id}`} className="data-type-header">
                      <div className="data-type-color" style={{ backgroundColor: dataType.color }}></div>
                      <div className="data-type-label">{dataType.label}</div>
                      <div 
                        className="toggle-cell"
                        onClick={() => toggleScenarioVisibility(scenario.id, dataType.id)}
                        title={`${isActive ? 'Hide' : 'Show'} ${dataType.label} for ${scenario.name}`}
                      >
                        <div 
                          className={`toggle-block ${!isActive ? 'disabled' : ''}`}
                          style={getToggleBlockStyle(isActive, dataType.color)}
                        >
                          {!isActive && <div className="slash" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Desktop: Data type toggle cells - hidden on mobile */}
              <div className="desktop-data-toggles">
              {dataTypes.map(dataType => {
                const isActive = settings[dataType.id];
                
                return (
                  <div 
                    key={`desktop-${scenario.id}-${dataType.id}`}
                    className="toggle-cell"
                    onClick={() => toggleScenarioVisibility(scenario.id, dataType.id)}
                    title={`${isActive ? 'Hide' : 'Show'} ${dataType.label} for ${scenario.name}`}
                  >
                    <div 
                      className={`toggle-block ${!isActive ? 'disabled' : ''}`}
                      style={getToggleBlockStyle(isActive, dataType.color)}
                    >
                      {!isActive && <div className="slash" />}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          );
        })}
      </div>

      {/* NPV Calculation Toggle - discount rate now managed via tooltip */}
      <div className="npv-toggle">
        <div className="form-group">
          <EnhancedToggle
            checked={globalSettings.calculateNpv}
            onChange={(e) => handleGlobalSettingChange('calculateNpv', e.target.checked)}
            size="small"
            color="primary"
            label={`Net Present Value (${globalSettings.discountRate}%)`}
            labelPlacement="start"
          />
        </div>
      </div>
      {/* --- COLA Options Section --- */}
      <div className="cola-settings" style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
        <h4>COLA Options</h4>
        
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {/* Salary COLA Toggle and Input */}
          <div className="cola-option">
            <div className="cola-toggle-row">
                <EnhancedToggle
                  checked={globalSettings.showColaWhileEmployed}
                  onChange={(e) => handleCheckboxChange('showColaWhileEmployed', e.target.checked)}
                  size="small"
                  color="primary"
                  label="Salary COLA"
                title="Cost of Living Adjustment applied to salary while employed. Default is 2.1% based on historical federal pay increases."
              />
              {globalSettings.showColaWhileEmployed && (
                <div className="cola-input-container">
                  <input 
                    type="number" 
                    step="0.1"
                    min="0"
                    max="10"
                    value={globalSettings.colaWhileEmployed}
                    onChange={(e) => handleGlobalSettingChange('colaWhileEmployed', parseFloat(e.target.value))}
                    className="cola-input"
                    style={{
                      width: '60px',
                      marginLeft: '10px',
                      padding: '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                  />
                  <span style={{ marginLeft: '5px' }}>%</span>
                  <div className="cola-tooltip">
                    <i className="info-icon" style={{ marginLeft: '8px', cursor: 'help', fontSize: '16px' }}>ⓘ</i>
                    <span className="tooltip-text">
                      Cost of Living Adjustment applied to salary while employed. Default is 2.1% based on historical federal pay increases.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Pension COLA Toggle and Input */}
          <div className="cola-option">
            <div className="cola-toggle-row">
                <EnhancedToggle
                  checked={globalSettings.showAnnualCola}
                  onChange={(e) => handleCheckboxChange('showAnnualCola', e.target.checked)}
                  size="small"
                  color="primary"
                  label="Pension COLA"
                title="Cost of Living Adjustment applied to pension after retirement. Default is 1.72% based on historical FERS COLA averages."
              />
              {globalSettings.showAnnualCola && (
                <div className="cola-input-container">
                  <input 
                    type="number" 
                    step="0.1"
                    min="0"
                    max="10"
                    value={globalSettings.annualCola}
                    onChange={(e) => handleGlobalSettingChange('annualCola', parseFloat(e.target.value))}
                    className="cola-input"
                    style={{
                      width: '60px',
                      marginLeft: '10px',
                      padding: '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                  />
                  <span style={{ marginLeft: '5px' }}>%</span>
                  <div className="cola-tooltip">
                    <i className="info-icon" style={{ marginLeft: '8px', cursor: 'help', fontSize: '16px' }}>ⓘ</i>
                    <span className="tooltip-text">
                      Cost of Living Adjustment applied to pension after retirement. Default is 1.72% based on historical FERS COLA averages.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Social Security COLA Toggle and Input */}
          <div className="cola-option">
            <div className="cola-toggle-row">
                <EnhancedToggle
                  checked={globalSettings.showSsCola}
                  onChange={(e) => handleCheckboxChange('showSsCola', e.target.checked)}
                  size="small"
                  color="primary"
                  label="Social Security COLA"
                title="Cost of Living Adjustment applied to Social Security benefits. Default is 2.5% based on historical Social Security COLA averages."
              />
              {globalSettings.showSsCola && (
                <div className="cola-input-container">
                  <input 
                    type="number" 
                    step="0.1"
                    min="0"
                    max="10"
                    value={globalSettings.ssCola}
                    onChange={(e) => handleGlobalSettingChange('ssCola', parseFloat(e.target.value))}
                    className="cola-input"
                    style={{
                      width: '60px',
                      marginLeft: '10px',
                      padding: '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                  />
                  <span style={{ marginLeft: '5px' }}>%</span>
                  <div className="cola-tooltip">
                    <i className="info-icon" style={{ marginLeft: '8px', cursor: 'help', fontSize: '16px' }}>ⓘ</i>
                    <span className="tooltip-text">
                      Cost of Living Adjustment applied to Social Security benefits. Default is 2.5% based on historical Social Security COLA averages.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* --- End COLA Options Section --- */}
    </div>
  );
};

export default ChartDisplayOptions;
